"use client"

import type { ComponentProps } from "react"
import { H5, H6 } from "~/components/common/heading"
import { Icon } from "~/components/common/icon"
import { Stack } from "~/components/common/stack"
import { Tooltip } from "~/components/common/tooltip"
import { BuiltWith } from "~/components/web/built-with"
import { ExternalLink } from "~/components/web/external-link"
import { NavLink, navLinkVariants } from "~/components/web/ui/nav-link"
import { Logo } from "~/components/web/ui/logo"
import { cx } from "~/utils/cva"
import { config } from "~/config"

type FooterProps = ComponentProps<"div"> & {
  hideNewsletter?: boolean
}

export const Footer = ({ children, className, hideNewsletter, ...props }: FooterProps) => {
  return (
    <footer
      className="flex flex-col gap-y-8 mt-auto pt-8 border-t border-foreground/10 md:pt-10 lg:pt-12"
      {...props}
    >
      <div
        className={cx("grid grid-cols-3 gap-y-8 gap-x-4 md:gap-x-6 md:grid-cols-16", className)}
        {...props}
      >
        <Stack
          direction="column"
          className="flex flex-col items-start gap-4 col-span-full md:col-span-6"
        >
          <Stack size="lg" direction="column" className="min-w-0 max-w-64">
            <Logo />
            <p className="text-sm text-muted-foreground">
              {config.site.description}
            </p>

          </Stack>

          <Stack className="text-xl opacity-75">
            <Tooltip tooltip="Contact us">
              <ExternalLink href={`mailto:${config.site.email}`} className={navLinkVariants()}>
                <Icon name="lucide/mail" />
              </ExternalLink>
            </Tooltip>

            <Tooltip tooltip="Follow us on X/Twitter">
              <ExternalLink href={config.links.twitter} className={navLinkVariants()}>
                <Icon name="tabler/brand-x" />
              </ExternalLink>
            </Tooltip>
          </Stack>

          {/* <div className="flex flex-row flex-wrap items-end justify-between gap-x-4 gap-y-2 w-full text-sm text-muted-foreground **:[&[href]]:font-medium **:[&[href]]:text-foreground **:[&[href]]:hover:text-secondary-foreground">
            <BuiltWith medium="footer" />
          </div> */}
        </Stack>

        <Stack direction="column" className="text-sm md:col-span-3 md:col-start-8">
          <H6 as="strong">Quick Links</H6>

          <NavLink href="/resources">Resources</NavLink>
          <NavLink href="/resources/books">Books</NavLink>
          <NavLink href="/resources/tools">Tools</NavLink>
          <NavLink href="/query-filter">Query Filter</NavLink>
          <NavLink href="/submit">👉 Submit</NavLink>
        </Stack>
      </div>

      <div className="flex flex-row flex-wrap items-end justify-between gap-x-4 gap-y-2 w-full text-sm text-muted-foreground **:[&[href]]:font-medium **:[&[href]]:text-foreground **:[&[href]]:hover:text-secondary-foreground">
        <BuiltWith medium="footer" />
        <p>
          Website may contain affiliate links.
        </p>
      </div>

      {children}
    </footer>
  )
}
